/**
 * Logfire Browser Observability Configuration
 *
 * Configures Logfire for frontend observability with automatic instrumentation
 * for HTTP requests, user interactions, and performance metrics.
 *
 * Traces are sent to the backend proxy endpoint (/client-traces) which forwards
 * them to Logfire with proper authentication, keeping the write token secure.
 */

import { getWebAutoInstrumentations } from "@opentelemetry/auto-instrumentations-web";
import * as logfire from "@pydantic/logfire-browser";

import { clientTraceHeaders } from "./clientTraceAuth";
import { getAccessToken } from "./supabase";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

if (!BACKEND_URL) {
  throw new Error("VITE_BACKEND_URL environment variable is required in production");
}
const LOGFIRE_ENABLED = import.meta.env.VITE_LOGFIRE_ENABLED === "true";
const ENVIRONMENT = import.meta.env.VITE_LOGFIRE_ENVIRONMENT || "local";

let isInitialized = false;

export type BrowserServiceName = "web" | "add-in";

/**
 * Initialize Logfire browser observability
 *
 * Should be called once when the app starts (in App.tsx useEffect).
 * Configures automatic instrumentation for:
 * - HTTP fetch/XHR requests
 * - User interactions (clicks, navigation)
 * - Performance metrics
 * - Page loads
 */
export function initializeLogfire(serviceName: BrowserServiceName): void {
  // Skip initialization if Logfire is disabled or already initialized
  if (!LOGFIRE_ENABLED) {
    return;
  }

  if (isInitialized) {
    console.warn("[Logfire] Already initialized, skipping duplicate initialization");
    return;
  }

  try {
    logfire.configure({
      // Send traces to backend proxy (keeps token secure)
      traceUrl: `${BACKEND_URL}/client-traces`,

      // Resolve credentials at export time: app routes use the Supabase session, while a signed-out
      // public interview uses the server-minted scope proof registered after its info loads.
      traceExporterHeaders: (): Record<string, string> => {
        return clientTraceHeaders(window.location.pathname, getAccessToken());
      },

      // Service identification
      serviceName,
      environment: ENVIRONMENT,

      // Automatic instrumentation
      instrumentations: [
        getWebAutoInstrumentations({
          "@opentelemetry/instrumentation-fetch": {
            propagateTraceHeaderCorsUrls: [new RegExp(BACKEND_URL)],
            ignoreUrls: [/client-traces/],
            clearTimingResources: true,
          },
          "@opentelemetry/instrumentation-xml-http-request": {
            propagateTraceHeaderCorsUrls: [new RegExp(BACKEND_URL)],
            ignoreUrls: [/client-traces/],
          },
          "@opentelemetry/instrumentation-document-load": {
            enabled: false,
          },
          "@opentelemetry/instrumentation-user-interaction": {
            enabled: false,
          },
        }),
      ],

      // Diagnostic logging (verbose in dev, errors-only in prod)
      diagLogLevel: import.meta.env.DEV ? logfire.DiagLogLevel.ALL : logfire.DiagLogLevel.ERROR,
    });

    isInitialized = true;
  } catch (error) {
    console.error("[Logfire] Failed to initialize:", error);
    // Don't throw - app should continue even if observability fails
  }
}

/**
 * Check if Logfire is enabled and initialized
 */
export function isLogfireEnabled(): boolean {
  return LOGFIRE_ENABLED && isInitialized;
}

// Re-export logfire methods for direct use
export { logfire };
